import React, { useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";

const Category = ({
  categories = [], // Default to an empty array
  setCategories,
  title = "Category", // Default title
  options = [], // Default options
  isDropdown = false, // Flag to enable dropdown input
  commaAutoCreate = false, // Enables comma-separated auto-creation (like hashtags)
  autoFillNext = false, // Enables auto-fill of next range start based on last entry
}) => {
  const [inputValue, setInputValue] = useState("");
  const [rangeError, setRangeError] = useState("");

  // Parse the trailing number from a range string like "1-15000" → 15000
  const parseRangeEnd = (str) => {
    const match = str.trim().match(/(\d+)\s*$/);
    return match ? parseInt(match[1], 10) : null;
  };

  // Parse a "min-max" range string → { start, end } or null
  const parseRange = (str) => {
    const match = str.trim().match(/^(\d+)-(\d+)$/);
    if (!match) return null;
    return { start: parseInt(match[1], 10), end: parseInt(match[2], 10) };
  };

  // Check if newRange overlaps any existing range in the list
  const findOverlap = (newRange, existingList) => {
    for (const item of existingList) {
      const r = parseRange(item);
      if (!r) continue;
      if (!(newRange.end < r.start || newRange.start > r.end)) return item;
    }
    return null;
  };

  const handleAddCategory = (valueOverride) => {
    const val = (valueOverride !== undefined ? valueOverride : inputValue).trim();
    if (!val || categories.includes(val)) return;

    // Range validation for age / followers count categories
    if (autoFillNext) {
      const newRange = parseRange(val);
      if (!newRange) {
        setRangeError(`Please enter a valid range in "min-max" format (e.g. 18-30).`);
        return;
      }
      if (newRange.start >= newRange.end) {
        setRangeError("The start value must be less than the end value.");
        return;
      }
      const overlapping = findOverlap(newRange, categories);
      if (overlapping) {
        setRangeError(`Overlaps with existing range "${overlapping}". Ranges must not overlap.`);
        return;
      }
    }

    setRangeError("");
    setCategories([...categories, val]);

    // Auto-fill next range start for Age / Followers Count
    if (autoFillNext) {
      const endNum = parseRangeEnd(val);
      setInputValue(endNum !== null ? `${endNum + 1}-` : "");
    } else {
      setInputValue("");
    }
  };

  const handleRemoveCategory = (index) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const handleDropdownChange = (value) => {
    if (value === "All") {
      setCategories([...new Set([...categories, ...options.filter(opt => opt !== "All")])]);
    } else {
      setInputValue(value);
    }
  };

  // Handle typing: auto-add when comma is typed (commaAutoCreate mode)
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (rangeError) setRangeError("");
    if (commaAutoCreate && val.endsWith(",")) {
      const item = val.slice(0, -1).trim();
      if (item && !categories.map(c => c.toLowerCase()).includes(item.toLowerCase())) {
        setCategories([...categories, item]);
      }
      setInputValue("");
    } else {
      setInputValue(val);
    }
  };

  // Handle paste: split by comma and add all parts at once (commaAutoCreate mode)
  const handlePaste = (e) => {
    if (!commaAutoCreate) return;
    const pasted = e.clipboardData.getData("text");
    const parts = pasted.split(",").map(s => s.trim()).filter(Boolean);
    if (parts.length <= 1) return;
    e.preventDefault();
    const existingLower = categories.map(c => c.toLowerCase());
    const newItems = parts.filter(p => !existingLower.includes(p.toLowerCase()));
    if (newItems.length > 0) setCategories([...categories, ...newItems]);
    setInputValue("");
  };

  return (
    <section className="p-4 border rounded-md bg-blue-50 shadow-md mt-4">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Input Field or Dropdown */}
        <div className="flex-1">
          {isDropdown ? (
            <select
              className="w-full border px-4 py-2 rounded-md"
              value={inputValue}
              onChange={(e) => handleDropdownChange(e.target.value)}
            >
              <option value="">Select an option</option>
              {options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if ((e.key === "Tab" || e.key === "Enter") && inputValue.trim()) {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
              className="w-full border px-4 py-2 rounded-md"
              placeholder={
                commaAutoCreate
                  ? `Enter ${title} — separate with comma`
                  : autoFillNext
                  ? `Enter ${title} (e.g. 1-15000)`
                  : `Enter ${title}`
              }
            />
          )}
        </div>

        {/* Add Button */}
        <button
          type="button"
          onClick={() => handleAddCategory()}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500"
        >
          <FaPlus /> Add
        </button>
      </div>

      {/* Range error message */}
      {rangeError && (
        <p className="text-red-500 text-sm mt-2">{rangeError}</p>
      )}

      {/* Category List */}
      <div className="flex flex-wrap gap-2 mt-4">
        {categories.length > 0 ? (
          categories.map((item, index) => (
            <span
              key={index}
              className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-2"
            >
              {item}
              <button
                type="button"
                onClick={() => handleRemoveCategory(index)}
                className="text-red-600 hover:text-red-800"
              >
                <FaTrash />
              </button>
            </span>
          ))
        ) : (
          <p className="text-gray-500">No {title.toLowerCase()} added yet.</p>
        )}
      </div>
    </section>
  );
};

export default Category;
